/**
 * Config tab (§4.4.2).
 *
 * ConfigForm emits a minimal PATCH delta + pre-built YAML diff pair.
 * We show the diff in a modal; Apply fires the mutation which returns
 * a 202 / job_id and kicks the deploy drawer.
 */
import { useState } from 'react';
import type { AgentConfig, UpdateAgentPayload } from '@/api/agents';
import { useUpdateAgent } from '@/hooks/useAgent';
import { ConfigForm } from '@/components/ConfigForm';
import { YamlDiffView } from '@/components/YamlDiffView';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ApiError } from '@/api/client';

interface ConfigProps {
  agent: AgentConfig;
}

interface PendingChange {
  patch: UpdateAgentPayload;
  yaml: { before: string; after: string };
  version: string;
}

export default function Config({ agent }: ConfigProps) {
  const [pending, setPending] = useState<PendingChange | null>(null);
  const [error, setError] = useState<string | null>(null);
  const updateAgent = useUpdateAgent(agent.name);

  async function handleApply() {
    if (!pending) return;
    setError(null);
    try {
      await updateAgent.mutateAsync({
        version: pending.version,
        payload: pending.patch,
      });
      setPending(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to apply changes');
    }
  }

  return (
    <>
      <ConfigForm
        initial={agent}
        onReview={(patch, yaml, version) => {
          setPending({ patch, yaml, version });
          setError(null);
        }}
      />
      <Dialog
        open={!!pending}
        onOpenChange={(open) => {
          if (!open) setPending(null);
        }}
      >
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Review changes</DialogTitle>
            <DialogDescription>
              These are the YAML diffs that will be written to <code>config.yaml</code>.
            </DialogDescription>
          </DialogHeader>
          {pending && (
            <YamlDiffView before={pending.yaml.before} after={pending.yaml.after} />
          )}
          {error && (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPending(null)} disabled={updateAgent.isPending}>
              Cancel
            </Button>
            <Button onClick={handleApply} disabled={updateAgent.isPending}>
              {updateAgent.isPending ? 'Applying…' : 'Apply'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
